import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import logo from "@/assets/bubble-radar-logo.png";
import { LogOut } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = logo;
const LOGIN_BOOTSTRAP_KEY = "bubble-radar-login-bootstrap";

export default function ProfilePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [me, setMe] = useState(null);
  const [portalError, setPortalError] = useState("");

  const showStartSubscription = !me?.billingStatus || me?.billingStatus === "none";

  const fetchMe = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/me`, { withCredentials: true });
      setMe(resp.data);
    } catch {
      setMe(null);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const openPortal = async () => {
    setLoading(true);
    setPortalError("");
    try {
      const resp = await axios.post(
        `${API}/stripe/create-portal-session`,
        { origin: window.location.origin },
        { withCredentials: true },
      );
      if (resp.data?.url) {
        window.location.href = resp.data.url;
      } else {
        const detail = resp.data?.detail || "Unable to open billing portal";
        setPortalError(detail);
        toast.error(detail);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Unable to open billing portal";
      setPortalError(detail);
      toast.error(detail);
    }
    setLoading(false);
  };

  const handleSignOut = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Continue with local cleanup even if server-side logout fails.
    }

    window.sessionStorage.removeItem(LOGIN_BOOTSTRAP_KEY);
    toast.success("Signed out");
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900" data-testid="profile-title">
              My Profile
            </h1>
            <p className="text-sm text-slate-500" data-testid="profile-subtitle">
              Manage subscription preferences and account access.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              data-testid="profile-back-button"
              variant="outline"
              onClick={() => navigate("/app")}
            >
              Back to Dashboard
            </Button>
            <Button
              data-testid="profile-signout-button"
              variant="outline"
              onClick={handleSignOut}
              className="border-slate-200 text-slate-600"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>

        <Card className="mb-6" data-testid="profile-card">
          <CardHeader>
            <CardTitle className="text-base" data-testid="profile-card-title">Subscription</CardTitle>
            <div className="mt-4 flex justify-center" data-testid="profile-logo-wrapper">
              <img
                src={LOGO_URL}
                alt="Bubble Radar logo"
                data-testid="profile-logo"
                className="h-24 w-auto"
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600" data-testid="profile-account-summary">
              <div className="text-xs uppercase tracking-wide text-slate-400">Account</div>
              <div className="mt-2 flex flex-wrap gap-3">
                <span data-testid="profile-email">Email: {me?.email || "—"}</span>
                <span data-testid="profile-billing-status">Status: {me?.billingStatus || "—"}</span>
              </div>
            </div>

            {portalError ? (
              <div
                data-testid="profile-portal-error"
                className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
              >
                {portalError}
              </div>
            ) : null}

            {showStartSubscription ? (
              <div
                data-testid="profile-start-subscription-card"
                className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              >
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400">Subscription</div>
                  <div className="mt-1 font-medium text-slate-800">
                    Start a trial to manage billing from this page.
                  </div>
                </div>
                <Button
                  data-testid="profile-start-subscription-button"
                  onClick={() => navigate("/billing")}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  Start trial
                </Button>
              </div>
            ) : null}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Button
                data-testid="profile-renew-button"
                onClick={openPortal}
                className="w-full bg-slate-900 hover:bg-slate-800"
                disabled={loading}
              >
                {loading ? "Opening..." : "Renew"}
              </Button>
              <Button
                data-testid="profile-upgrade-button"
                onClick={openPortal}
                variant="outline"
                className="w-full"
                disabled={loading}
              >
                {loading ? "Opening..." : "Upgrade"}
              </Button>
              <Button
                data-testid="profile-cancel-button"
                onClick={openPortal}
                variant="outline"
                className="w-full text-rose-600 border-rose-200 hover:text-rose-700"
                disabled={loading}
              >
                {loading ? "Opening..." : "Cancel"}
              </Button>
            </div>

            <div
              data-testid="profile-support-note"
              className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700 text-center"
            >
              Thank you for subscribing. If you do not see your asset listed, please e-mail
              <span className="font-semibold"> support@bubbleradar.com</span> and we will try to add it as soon as possible.
              We are constantly improving the platform to provide you easy access to advanced intelligence.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
